import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { recallCartMeta } from "../lib/cartMeta";
import { checkoutRoute, PO_THRESHOLD_USD } from "../lib/checkoutPolicy";
import { OrderController } from "../controllers/order";
import { CustomerController } from "../controllers/customer";
import { checkCreditForOrder, allowedPaymentModes, customerOnTerms, availableCredit } from "../lib/credit";
import { config } from "../config";
import CheckoutShipping from "../components/CheckoutShipping";
import CheckoutPayment from "../components/CheckoutPayment";
import CheckoutReview from "../components/CheckoutReview";
import CheckoutSuccess from "../components/CheckoutSuccess";
import PaymentOverlay from "../components/PaymentOverlay";
import styles from "./Checkout.module.css";

const STEPS = ["shipping", "payment", "review", "done"];

export default function Checkout() {
  const { user, loading } = useAuth();
  const { items, setItems } = useCart();
  const { formatAmount } = useCurrency();
  const nav = useNavigate();

  const [step, setStep] = useState("shipping");
  const [shipping, setShipping] = useState({
    name: user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "",
    company: user?.company || "",
    email: user?.email || "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    method: "ground",
  });
  const [payment, setPayment] = useState({ mode: "card", poNumber: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    CustomerController.get().then((c) => { if (!cancelled) setCustomer(c || null); })
      .catch(() => { if (!cancelled) setCustomer(null); });
    return () => { cancelled = true; };
  }, []);

  const onTerms = customerOnTerms(customer);
  const modes = allowedPaymentModes(customer);
  const credit = availableCredit(customer);

  const lines = useMemo(() => items.map((it) => {
    const meta = recallCartMeta(it.sku) || {};
    const unit = meta.price ?? it.unitPrice ?? 0;
    return { ...it, meta, unit, amount: (Number(unit) || 0) * (it.qty || 0) };
  }), [items]);
  const subtotal = useMemo(() => lines.reduce((n, l) => n + l.amount, 0), [lines]);
  const route = checkoutRoute(subtotal);

  // Force PO mode when policy requires it OR customer is on terms (paying
  // by card on a terms account voids those terms).
  useEffect(() => {
    const cardAllowed = modes.includes("card");
    const mustPO = route === "po" || !cardAllowed;
    if (mustPO && payment.mode !== "po") setPayment((p) => ({ ...p, mode: "po" }));
  }, [route, modes.join(",")]); // eslint-disable-line

  // Pre-flight credit check: don't let the user proceed past payment if
  // the order would exceed their available credit on a terms account.
  const creditCheck = checkCreditForOrder(
    onTerms || payment.mode === "po" ? customer : null,
    subtotal,
  );

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!items.length && step !== "done") {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Checkout</h1>
        <p className={styles.muted}>
          Your cart is empty. <Link to="/items" className={styles.link}>Browse the catalog →</Link>
        </p>
      </div>
    );
  }

  function buildPayload(extra = {}) {
    return {
      items: lines.map((l) => ({
        sku: l.sku, qty: l.qty, unitPrice: l.unit, name: l.meta?.name,
      })),
      shipping,
      payment,
      customer: {
        id: user.id, email: user.email, company: user.company,
        priceLevel: user.priceLevel,
        terms: customer?.terms,
        creditLimit: customer?.creditLimit,
      },
      totals: { subtotal, currency: "USD", quantity: lines.reduce((n, l) => n + l.qty, 0) },
      ...extra,
    };
  }

  async function submit() {
    setError(null);
    if (!creditCheck.ok) { setError(creditCheck.message); return; }

    if (payment.mode === "card") {
      // Open the payment overlay; the gateway will call back via
      // onSuccess once the auth (or auth+capture) succeeds.
      setPaymentOpen(true);
      return;
    }

    // PO path — no card capture, just submit.
    setSubmitting(true);
    try {
      const result = await OrderController.submitPurchaseOrder(buildPayload());
      setSubmitted(result);
      setStep("done");
      setItems([]);
    } catch (e) {
      setError(e?.message || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onPaymentSuccess(tx) {
    setPaymentOpen(false);
    setSubmitting(true);
    setError(null);
    try {
      const result = await OrderController.submitSalesOrder(buildPayload({
        payment: { ...payment, transactionId: tx?.id || tx?.intent?.intentId, gateway: config.payments.gateway },
      }));
      setSubmitted(result);
      setStep("done");
      setItems([]);
    } catch (e) {
      setError(e?.message || "Payment captured but order submission failed. Please contact support.");
    } finally {
      setSubmitting(false);
    }
  }

  // Capture mode: respect order policy first, then env. Auth-only is
  // sensible for B2B when fulfilment lags by days; auth+capture suits
  // small same-day shipments.
  const captureMode = config.payments.captureMode === "auth_only" ? false : true;
  const intentRequest = paymentOpen ? {
    orderId: null,
    amount: Math.round(subtotal * 100) / 100,
    currency: "USD",
    capture: captureMode,
    customer: { id: user.id, email: user.email, name: shipping.name },
    items: lines.map((l) => ({ sku: l.sku, qty: l.qty, unitPrice: l.unit, name: l.meta?.name })),
    metadata: { route, priceLevel: user.priceLevel, terms: customer?.terms || "" },
  } : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Checkout</h1>
        <Stepper step={step} />
      </header>

      <div className={styles.layout}>
        <main className={styles.main}>
          {step === "shipping" && (
            <CheckoutShipping value={shipping} onChange={setShipping} onNext={() => setStep("payment")} />
          )}
          {step === "payment" && (
            <CheckoutPayment
              value={payment}
              onChange={setPayment}
              route={route}
              threshold={PO_THRESHOLD_USD}
              subtotal={subtotal}
              formatAmount={formatAmount}
              modes={modes}
              onTerms={onTerms}
              customer={customer}
              creditCheck={creditCheck}
              gatewayLabel={(config.payments.gateway || "bolt").replace(/^./, (c) => c.toUpperCase())}
              captureMode={config.payments.captureMode}
              onBack={() => setStep("shipping")}
              onNext={() => setStep("review")}
            />
          )}
          {step === "review" && (
            <CheckoutReview
              lines={lines}
              shipping={shipping}
              payment={payment}
              route={route}
              subtotal={subtotal}
              formatAmount={formatAmount}
              error={error}
              submitting={submitting}
              onBack={() => setStep("payment")}
              onSubmit={submit}
            />
          )}
          {step === "done" && submitted && (
            <CheckoutSuccess
              record={submitted}
              formatAmount={formatAmount}
              onContinue={() => nav("/items")}
              onViewOrders={() => nav("/orders")}
            />
          )}
        </main>

        {step !== "done" && (
          <aside className={styles.summary}>
            <CheckoutSummary
              lines={lines}
              subtotal={subtotal}
              formatAmount={formatAmount}
              route={route}
              credit={credit}
              creditCheck={creditCheck}
            />
          </aside>
        )}
      </div>

      <PaymentOverlay
        open={paymentOpen}
        gatewayId={config.payments.gateway}
        intentRequest={intentRequest}
        onSuccess={onPaymentSuccess}
        onError={(e) => setError(e?.message || "Payment failed")}
        onClose={() => setPaymentOpen(false)}
      />
    </div>
  );
}

function Stepper({ step }) {
  const labels = { shipping: "Shipping", payment: "Payment", review: "Review", done: "Submitted" };
  const idx = STEPS.indexOf(step);
  return (
    <ol className={styles.stepper}>
      {STEPS.map((s, i) => {
        const state = i < idx ? "done" : i === idx ? "active" : "upcoming";
        return (
          <li key={s} className={[styles.step, styles[state]].join(" ")}>
            <span className={styles.stepDot}>{i + 1}</span>
            <span className={styles.stepLabel}>{labels[s]}</span>
          </li>
        );
      })}
    </ol>
  );
}

function CheckoutSummary({ lines, subtotal, formatAmount, route, credit, creditCheck }) {
  return (
    <div className={styles.summaryCard}>
      <h2 className={styles.summaryHead}>Order Summary</h2>
      <ul className={styles.summaryList}>
        {lines.map((l) => (
          <li key={l.sku} className={styles.summaryLine}>
            <span className={styles.summaryName}>
              {l.meta?.name || l.sku} <span className={styles.summaryQty}>× {l.qty}</span>
            </span>
            <span>{formatAmount(l.amount)}</span>
          </li>
        ))}
      </ul>
      <div className={styles.summaryRow}>
        <span>Subtotal</span><strong>{formatAmount(subtotal)}</strong>
      </div>
      <div className={styles.summaryNote}>Shipping & tax calculated next.</div>
      {credit != null && (
        <div className={styles.creditRow}>
          <span>Available credit</span>
          <strong>{formatAmount(credit)}</strong>
        </div>
      )}
      {creditCheck && !creditCheck.ok && (
        <div className={styles.creditWarn}>
          <strong>Over credit limit by {formatAmount(creditCheck.overage || 0)}.</strong>
          <span>{creditCheck.message}</span>
        </div>
      )}
      {route === "po" && (
        <div className={styles.poBadge}>
          <strong>Purchase Order required.</strong> This order will route for internal approval.
        </div>
      )}
    </div>
  );
}
